/*
 * 会话状态。
 *
 * 边界（apps/web/README.md §状态划分）：
 *   - 这里只放 Session、当前角色摘要、宿主能力。
 *   - 会话数据、历史、记忆、关系走各自的资源 hook，不塞进这个 Context。
 *   - 禁止建立一个包含所有业务的全局 Store。
 *
 * 三条硬规则：
 *   1. **当前角色只能来自服务端。** 服务端从 Session 的 appId 反查
 *      Product → Character 后随上下文下发。前端不从 URL、域名、路径或本地
 *      配置推断 characterId。连不上时的处理是「不知道」，不是「默认缈缈」。
 *   2. **网络失败不等于未登录。** 只有 401 才把用户踢回未登录；offline /
 *      timeout 保持已登录并标记 degraded。反过来做是经典 bug：地铁里过隧道
 *      就被登出，回来还得重新验证码。
 *   3. **前端不持有 Token。** 凭据在 httpOnly Cookie 里，这个文件读不到，
 *      也不需要读。「有没有登录」的唯一判据是服务端下发的 status。
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { CharacterAppearanceView } from "@dreamverse/contracts";
import { dismissBoot } from "../boot";
import { ApiError } from "../services/api-client";
import {
  fetchSession,
  loginByMiniappCode,
  logout as logoutRequest,
  PRODUCT_MAINTENANCE_CODE,
  type LoginMethods,
  type SessionBootstrap,
} from "../services/auth-api";
import {
  getHostProfile,
  takeMiniappLoginCode,
  type HostProfile,
} from "../services/host-bridge";
import {
  LOCAL_ASSETS,
  OFFLINE_PREVIEW_ENABLED,
  PREVIEW_APPEARANCE,
} from "../services/offline-preview";

/**
 * apps/web/src/features/auth/README.md §状态 定义的八态。
 *
 * ⚠ `maintenance` 是唯一一个**与登录状态无关**的：它描述的是入口，不是这个人。
 * 拿到它就整屏切维护页，`RequireSession` 那一层根本走不到。
 */
export type AuthStatus =
  | "checking"
  | "anonymous"
  | "authenticating"
  | "needs_profile"
  | "authenticated"
  | "blocked"
  | "maintenance"
  | "error";

/** 服务端还没告诉我们支持哪些登录方式时的保守假设：只认手机号。 */
const UNKNOWN_LOGIN_METHODS: LoginMethods = {
  phone: true,
  wechatMiniapp: false,
  wechatOfficial: false,
  environment: "production",
  devImpersonation: false,
};

export interface SessionValue {
  status: AuthStatus;
  /**
   * 运营写的维护说明。只在 `status === "maintenance"` 时可能有值，
   * 而且**经常是 null**——运营没填就是 null。维护页自己有兜底句。
   */
  maintenanceNote: string | null;
  /** 维护页名牌显示的当前入口角色名；没有配置角色时为 null。 */
  characterDisplayName: string | null;
  /** 服务端下发的角色外观。拿不到就是 null——不填夹具，不填默认值。 */
  appearance: CharacterAppearanceView | null;
  /** 聊天每轮按权威 formKey 下发外观后，用它同步全站当前角色形态。 */
  syncAppearance: (appearance: CharacterAppearanceView) => void;
  conversationId: string | null;
  accountDisplayName: string | null;
  /**
   * **用户自己的**头像。没上传过 / 没绑过微信就是 null。
   *
   * ⚠ 不要拿 `useAppearance().avatarUrl` 顶替它——那是**角色**的头像。
   * 右上角那个入口指向「我的」，画角色的脸等于把用户的位置让给了她。
   * 拿不到就画占位图标，别回落到角色头像，也别回落到离线预览那张。
   */
  accountAvatarUrl: string | null;
  /** 只控制 `/` 的落点，不关闭或改写其他页面。 */
  knowledgeQaHome: boolean;
  /** 已登录但会话数据取不到/已陈旧。界面据此显示降级横幅。 */
  degraded: boolean;
  error: ApiError | null;
  host: HostProfile;
  /** 服务端支持的登录方式。登录页据此渲染，与宿主能力求交集。 */
  loginMethods: LoginMethods;
  reload: () => void;
  /**
   * 登录成功后由登录页调用，把服务端返回的会话直接装进 Context。
   * 不再多发一次 `GET session`——那一次往返里界面是空的，用户会看到闪烁。
   */
  adopt: (bootstrap: SessionBootstrap) => void;
  /** 进入「登录中」。用于按钮置灰与拦住重复提交。 */
  beginLogin: () => void;
  /** 登录失败后退回未登录。不清空已知的角色外观，登录页还要用它。 */
  cancelLogin: () => void;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const host = useMemo(() => getHostProfile(), []);
  const [status, setStatus] = useState<AuthStatus>("checking");
  const [appearance, setAppearance] = useState<CharacterAppearanceView | null>(
    null,
  );
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [accountDisplayName, setAccountDisplayName] = useState<string | null>(
    null,
  );
  const [accountAvatarUrl, setAccountAvatarUrl] = useState<string | null>(null);
  const [knowledgeQaHome, setKnowledgeQaHome] = useState(false);
  const [loginMethods, setLoginMethods] = useState<LoginMethods>(
    UNKNOWN_LOGIN_METHODS,
  );
  const [degraded, setDegraded] = useState(false);
  const [maintenanceNote, setMaintenanceNote] = useState<string | null>(null);
  const [characterDisplayName, setCharacterDisplayName] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [nonce, setNonce] = useState(0);

  const adopt = useCallback((data: SessionBootstrap) => {
    setStatus(data.status);
    setAppearance(data.appearance ?? null);
    setConversationId(data.conversationId ?? null);
    setAccountDisplayName(data.displayName ?? null);
    setAccountAvatarUrl(data.avatarUrl ?? null);
    setKnowledgeQaHome(data.knowledgeQaHome === true);
    setLoginMethods(data.loginMethods);
    setMaintenanceNote(data.maintenanceNote ?? null);
    setCharacterDisplayName(data.characterDisplayName ?? null);
    setDegraded(false);
    setError(null);
  }, []);
  const syncAppearance = useCallback(
    (next: CharacterAppearanceView) => setAppearance(next),
    [],
  );

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    // 小程序壳把 wx.login 的 code 挂在地址上传进来时，先拿它换会话。
    // 换不成再走普通引导——壳给的 code 可能已经用过或过期，
    // 那不该让整个应用停在「检查中」。
    const bootstrap = async (): Promise<SessionBootstrap> => {
      const miniappCode = takeMiniappLoginCode();
      if (!miniappCode) return fetchSession(controller.signal);

      try {
        return await loginByMiniappCode(miniappCode);
      } catch {
        return fetchSession(controller.signal);
      }
    };

    void bootstrap()
      .then((data) => {
        if (cancelled) return;
        adopt(data);
      })
      .catch((cause: unknown) => {
        if (cancelled || controller.signal.aborted) return;
        const failure =
          cause instanceof ApiError
            ? cause
            : new ApiError({
                kind: "malformed",
                message: String(cause),
                requestId: "-",
                retryable: false,
              });

        setError(failure);

        // ⚠ 必须排在下面那个「一律当作已登录 + 降级」的兜底之前。
        //
        // 维护态的 503 在 ApiError 里是 `kind: "http"`，而那个兜底会把
        // 任何 http 失败读成「判定不了登录状态，先按登录放行」——正好是放行。
        // 首屏走 `GET session` 时不会到这儿（那条路由回 200），但小程序壳
        // 那条先登录的支路、以及登录页上的重试都会。
        if (failure.code === PRODUCT_MAINTENANCE_CODE) {
          setStatus("maintenance");
          const note = failure.details["note"];
          setMaintenanceNote(typeof note === "string" && note ? note : null);
          const displayName = failure.details["characterDisplayName"];
          setCharacterDisplayName(
            typeof displayName === "string" && displayName ? displayName : null,
          );
          setDegraded(false);
          return;
        }

        if (failure.kind === "unauthorized") {
          setStatus("anonymous");
          setCharacterDisplayName(null);
          setAppearance(null);
          setConversationId(null);
          setKnowledgeQaHome(false);
          setDegraded(false);
          return;
        }

        if (failure.kind === "forbidden") {
          setStatus("blocked");
          setDegraded(false);
          return;
        }

        // offline / timeout / http / malformed：
        // 无法判定登录状态，保持之前的判断并标记降级。
        //
        // 首次进入就连不上时，这里假定「凭证仍在 httpOnly Cookie 里」而按已登录
        // 呈现——因为客户端读不到 httpOnly Cookie，无从证伪。真正的裁定发生在
        // 第一次成功请求：若那次返回 401，上面的分支会立刻把用户切回未登录。
        setStatus("authenticated");
        setDegraded(true);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [nonce, adopt]);

  /*
   * 会话一判定出结果就撤掉首屏加载画面。
   *
   * 放在这里而不是 `main.tsx` 的 rAF 里：那个时机是「React 画完了」，
   * 而那一刻 `RequireSession` 画的还是 `checking` 分支的纯色底，撤掉等于
   * 让五只蝴蝶突然消失、换成一块空白。理由与兜底见 `boot.ts`。
   *
   * 每条分支（成功 / unauthorized / forbidden / 降级）都会把 status 挪走，
   * 所以这条 effect 一定会命中一次。
   */
  useEffect(() => {
    if (status !== "checking") dismissBoot();
  }, [status]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);  const beginLogin = useCallback(() => setStatus("authenticating"), []);
  const cancelLogin = useCallback(() => setStatus("anonymous"), []);

  const logout = useCallback(async () => {
    // 先请服务端撤销 Refresh Session，再清本地状态。
    // 顺序反过来的话，撤销失败时界面已经显示未登录，而那个会话其实还活着。
    try {
      const result = await logoutRequest();
      setLoginMethods(result.loginMethods);
    } catch {
      // 撤销失败（比如离线）也要放用户走，但会话仍在服务端有效，
      // 下次联网时 GET session 会把他恢复成已登录——这是诚实的表现。
    }
    setStatus("anonymous");
    setMaintenanceNote(null);
    setCharacterDisplayName(null);
    setAppearance(null);
    setConversationId(null);
    setAccountDisplayName(null);
    setAccountAvatarUrl(null);
    setKnowledgeQaHome(false);
    setDegraded(false);
    setError(null);
  }, []);

  const value = useMemo<SessionValue>(
    () => ({
      status,
      maintenanceNote,
      characterDisplayName,
      appearance,
      syncAppearance,
      conversationId,
      accountDisplayName,
      accountAvatarUrl,
      knowledgeQaHome,
      degraded,
      error,
      host,
      loginMethods,
      reload,
      adopt,
      beginLogin,
      cancelLogin,
      logout,
    }),
    [
      status,
      maintenanceNote,
      characterDisplayName,
      appearance,
      syncAppearance,
      conversationId,
      accountDisplayName,
      accountAvatarUrl,
      knowledgeQaHome,
      degraded,
      error,
      host,
      loginMethods,
      reload,
      adopt,
      beginLogin,
      cancelLogin,
      logout,
    ],
  );

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession 必须在 SessionProvider 内使用");
  return value;
}

/**
 * 当前角色的外观。
 *
 * 三段回落：服务端下发 > 本地兜底资产 > 明确的「不知道」。
 *
 * 夹具只提供展示名与图片，不提供 characterId。因此任何需要真实角色身份的动作
 * ——发消息、扣灵力、写记忆——都不会因为这个回落而误执行到某个角色身上，
 * 它们各自会因为拿不到 id 而明确失败。
 */
export interface AppearanceView {
  /** 含形态前缀的完整展示名，直接拿去渲染。 */
  name: string;
  baseName: string;
  prefix: string;
  portraitUrl: string | null;
  avatarUrl: string | null;
  backgroundUrl: string;
  /** 名字与图片来自本地兜底而非服务端。界面据此打「离线预览」标记。 */
  fromPreview: boolean;
}

export function useAppearance(): AppearanceView {
  const session = useSession();
  const server = session.appearance;

  const preview = OFFLINE_PREVIEW_ENABLED ? PREVIEW_APPEARANCE : null;
  const baseName = server?.displayName ?? preview?.displayName ?? "角色待下发";
  const prefix = server?.namePrefix ?? "";

  return {
    name: `${prefix}${baseName}`,
    baseName,
    prefix,
    portraitUrl: server?.defaultPortraitUrl ?? preview?.portraitUrl ?? null,
    avatarUrl: server?.avatarUrl ?? preview?.avatarUrl ?? null,
    // 背景与角色身份无关，没有也得有一张，否则舞台是一片空白。
    backgroundUrl:
      server?.backgroundUrl ?? preview?.backgroundUrl ?? LOCAL_ASSETS.background,
    fromPreview: server === null && preview !== null,
  };
}
